import i18n from 'i18next';
import { enqueueSnackbar } from 'notistack';
import { ModifyUserInfoProps } from 'types';
import { nav } from 'utils';
import { create } from 'zustand';
import { shallow } from 'zustand/shallow';
import useAPIStore from '../APIStore';
import useSystemStore from '../SystemStore';

interface TokenProps {
	companyCode: string;
	tokenType: string;
	sessionToken: string;
	refreshToken: string;
	sessionTokenExpiresAt: number;
	refreshTokenExpiresAt: number;
}

interface UserInfoProps {
	birthday: number;
	id: string;
	mail: string;
	nickname: string;
	phoneNum: string | null;
	sex: string;
	userPortrait: string | null;
}

interface UserStates {
	appToken: TokenProps | undefined;
	userToken: TokenProps | undefined;
	openToken: string | undefined;
	userInfo: UserInfoProps | undefined;
	isWeChatUser: boolean;
}

interface UserActions {
	actions: {
		initToken: () => void;
		setOpenToken: (token: string | null) => void;
		userLogin: (email: string, password: string) => void;
		userGoogleLogin: (authCode: string) => void;
		userWeChatLogin: (authCode: string) => void;
		acquireUserInfo: () => void;
		changeUserInfo: (data: ModifyUserInfoProps) => Promise<void>;
		clearUserInfo: () => void;
		resetUserPassword: (
			mail: string,
			pwd: string,
			captcha: string,
			tkId: string
		) => Promise<void>;
		changeUserPassword: (oldPwd: string, newPwd: string) => Promise<void>;
		setToken: (
			type: 'userToken' | 'appToken',
			token: TokenProps | undefined
		) => void;
		setIsWeChatUser: (isWeChatUser: boolean) => void;
		getToken: (type: 'userToken' | 'appToken') => TokenProps | undefined;
		acquireToken: () => string;
		removeToken: (type: 'userToken' | 'appToken') => void;
		destroyAccount: (captcha: string) => Promise<void>;
		displayUserAvatar: () => string;
	};
}

const useUserStore = create<UserStates & UserActions>((set, get) => ({
	appToken: undefined,
	userToken: undefined,
	openToken: undefined,
	userInfo: undefined,
	isWeChatUser: false,
	actions: {
		initToken: async () => {
			let appTokenRes: { data: TokenProps } | undefined;
			let userTokenRes: { data: TokenProps } | undefined;
			try {
				let appToken = get().actions.getToken('appToken');
				let userToken = get().actions.getToken('userToken');
				if (
					!appToken ||
					new Date(appToken.refreshTokenExpiresAt - 30000) <
						new Date()
				) {
					appTokenRes = await useAPIStore
						.getState()
						.api.getSessionToken();
				} else {
					appTokenRes = await useAPIStore
						.getState()
						.api.refreshToken(appToken.refreshToken);
				}
				if (
					userToken &&
					new Date(userToken.refreshTokenExpiresAt - 30000) >
						new Date()
				) {
					userTokenRes = await useAPIStore
						.getState()
						.api.refreshToken(userToken.refreshToken);
				} else {
					userToken = undefined;
				}
				get().actions.setToken(
					'appToken',
					appTokenRes ? appTokenRes.data : undefined
				);
				const userOpenToken = get().openToken;
				userToken =
					userOpenToken && !userToken
						? {
								companyCode: 'axbsec',
								tokenType: 'Bearer',
								sessionToken: userOpenToken,
								sessionTokenExpiresAt: Date.now() + 86400000,
								refreshToken: '',
								refreshTokenExpiresAt: Date.now(),
						  }
						: userToken;
				get().actions.setToken(
					'userToken',
					userTokenRes
						? userTokenRes.data
						: userOpenToken
						? userToken
						: undefined
				);
				get().actions.setIsWeChatUser(userOpenToken ? true : false);
				if (userToken) await get().actions.acquireUserInfo();
			} catch (e) {
				if (e instanceof Error)
					enqueueSnackbar(e.message, { variant: 'error' });
			}
		},
		setOpenToken: (token) => {
			set(() => ({
				openToken: token ? token : undefined,
			}));
		},
		userLogin: async (email, password) => {
			let loginRes: { data: TokenProps } | undefined;
			loginRes = await useAPIStore.getState().api.login(email, password);
			get().actions.setToken(
				'userToken',
				loginRes ? loginRes.data : undefined
			);
			await get().actions.acquireUserInfo();
		},
		userGoogleLogin: async (authCode) => {
			const loginRes = await useAPIStore
				.getState()
				.api.googleLogin(authCode);
			const newUser = loginRes.data.newUser;
			const token = loginRes.data.openToken;
			get().actions.setToken(
				'userToken',
				token
					? {
							companyCode: 'axbsec',
							tokenType: 'Bearer',
							sessionToken: token,
							sessionTokenExpiresAt: Date.now() + 86400000,
							refreshToken: '',
							refreshTokenExpiresAt: Date.now(),
					  }
					: undefined
			);
			await get().actions.acquireUserInfo();
			newUser ? nav('/AddInfo') : nav('/');
		},
		userWeChatLogin: async (authCode) => {
			const token = await useAPIStore
				.getState()
				.api.WeChatLogin(authCode);
			get().actions.setIsWeChatUser(!!token);
			get().actions.setToken(
				'userToken',
				token
					? {
							companyCode: 'axbsec',
							tokenType: 'Bearer',
							sessionToken: token,
							sessionTokenExpiresAt: Date.now() + 86400000,
							refreshToken: '',
							refreshTokenExpiresAt: Date.now(),
					  }
					: undefined
			);
			await get().actions.acquireUserInfo();
			nav('/');
		},
		acquireUserInfo: async () => {
			let userInfoRes: { data: UserInfoProps } | undefined;
			userInfoRes = await useAPIStore.getState().api.getUserInfo();
			set(() => ({
				userInfo: userInfoRes ? userInfoRes.data : undefined,
			}));
		},
		changeUserInfo: async (data) => {
			try {
				await useAPIStore.getState().api.modifyUserInfo(data);
				await get().actions.acquireUserInfo();
				enqueueSnackbar(i18n.t('default.success'), {
					variant: 'success',
				});
			} catch (e) {
				if (e instanceof Error)
					enqueueSnackbar(e.message, { variant: 'error' });
				return Promise.reject();
			}
		},
		clearUserInfo: () => {
			set(() => ({ userInfo: undefined }));
		},
		resetUserPassword: async (mail, pwd, captcha, tkId) => {
			try {
				await useAPIStore
					.getState()
					.api.resetPassword(mail, pwd, captcha, tkId);
				enqueueSnackbar(i18n.t('default.success'), {
					variant: 'success',
				});
			} catch (e) {
				if (e instanceof Error)
					enqueueSnackbar(e.message, { variant: 'error' });
				return Promise.reject();
			}
		},
		changeUserPassword: async (oldPwd, newPwd) => {
			try {
				await useAPIStore.getState().api.changePassword(oldPwd, newPwd);
				enqueueSnackbar(i18n.t('default.success'), {
					variant: 'success',
				});
			} catch (e) {
				if (e instanceof Error)
					enqueueSnackbar(e.message, { variant: 'error' });
				return Promise.reject();
			}
		},
		setToken: (type, token) => {
			token &&
				localStorage.setItem(
					`${process.env.REACT_APP_ENV}_${type}`,
					JSON.stringify(token)
				);
			const KV: {
				appToken?: TokenProps | undefined;
				userToken?: TokenProps | undefined;
			} = {};
			KV[type] = token;
			set(() => KV);
		},
		setIsWeChatUser: (isWeChatUser) => {
			set(() => ({
				isWeChatUser: isWeChatUser,
			}));
		},
		getToken: (type) => {
			const tokenString = localStorage.getItem(
				`${process.env.REACT_APP_ENV}_${type}`
			);
			if (tokenString) return JSON.parse(tokenString);
		},
		acquireToken: () => {
			const userToken = get().userToken;
			const appToken = get().appToken;
			return userToken
				? `${userToken.tokenType} ${userToken.sessionToken}`
				: appToken
				? `${appToken.tokenType} ${appToken.sessionToken}`
				: '';
		},
		removeToken: (type) => {
			localStorage.removeItem(`${process.env.REACT_APP_ENV}_${type}`);
			get().actions.setToken(type, undefined);
		},
		destroyAccount: async (captcha) => {
			try {
				await useAPIStore.getState().api.destroy(captcha);
				get().actions.removeToken('userToken');
				get().actions.clearUserInfo();
				enqueueSnackbar(i18n.t('default.success'), {
					variant: 'success',
				});
			} catch (e) {
				if (e instanceof Error)
					enqueueSnackbar(e.message, { variant: 'error' });
				return Promise.reject();
			}
		},
		displayUserAvatar: () => {
			const userPortrait = get().userInfo?.userPortrait;
			return userPortrait
				? `${process.env.REACT_APP_DOMAIN_NAME}${process.env.REACT_APP_API_GET_IMAGE}?name=${userPortrait}`
				: useSystemStore
						.getState()
						.actions.getImage('portrait_icon.png');
		},
	},
}));

export const useToken = () =>
	useUserStore(
		(state) => ({
			appToken: state.appToken,
			userToken: state.userToken,
		}),
		shallow
	);
export const useUserInfo = () => useUserStore((state) => state.userInfo);
export const useUserActions = () => useUserStore((state) => state.actions);
export const useIsWeChatUser = () =>
	useUserStore((state) => state.isWeChatUser);

export default useUserStore;
